<!--
  AscendQE: calendar-only date range picker.

  v-model:from / v-model:to are 'yyyy-MM-dd' strings (same as the stock
  date-input). Non-working days come from /api/v2/leave/workweek and holidays
  from /api/v2/leave/holidays, so what is disabled matches the leave rules the
  server applies. Keyboard follows the WAI-ARIA date picker dialog pattern.
-->
<template>
  <div
    ref="root"
    class="aqe-drp"
    :class="{'aqe-drp--open': open, 'aqe-drp--invalid': !!error}"
  >
    <label class="oxd-label aqe-drp__label" :for="triggerId">
      {{ label }}
      <span v-if="required" class="aqe-drp__req">*</span>
    </label>
    <button
      :id="triggerId"
      ref="trigger"
      type="button"
      class="aqe-drp__trigger"
      aria-haspopup="dialog"
      :aria-expanded="String(open)"
      :aria-describedby="error ? errorId : undefined"
      @click="toggle"
      @keydown.down.prevent="openPicker"
    >
      <i class="oxd-icon bi-calendar3 aqe-drp__icon" aria-hidden="true"></i>
      <span v-if="from" class="aqe-drp__value">
        <span>{{ pretty(from) }}</span>
        <i class="oxd-icon bi-arrow-right aqe-drp__sep" aria-hidden="true"></i>
        <span :class="{'aqe-drp__muted': !to}">{{
          to ? pretty(to) : 'Select end date'
        }}</span>
      </span>
      <span v-else class="aqe-drp__placeholder">{{ placeholder }}</span>
      <span v-if="from && to" class="aqe-drp__badge">
        {{ workingDays }} working {{ workingDays === 1 ? 'day' : 'days' }}
      </span>
      <i class="oxd-icon bi-chevron-down aqe-drp__chev" aria-hidden="true"></i>
    </button>
    <span v-if="error" :id="errorId" class="aqe-drp__error" role="alert">{{
      error
    }}</span>

    <transition name="aqe-drp-pop">
      <div
        v-if="open"
        ref="popup"
        class="aqe-drp__popup"
        role="dialog"
        aria-modal="false"
        :aria-label="label"
        @keydown="onKeydown"
      >
        <div class="aqe-drp__presets" role="group" aria-label="Quick ranges">
          <button
            v-for="p in presets"
            :key="p.label"
            type="button"
            class="aqe-drp__preset"
            :disabled="!p.range"
            @click="applyPreset(p)"
          >
            {{ p.label }}
          </button>
        </div>

        <div class="aqe-drp__head">
          <button
            type="button"
            class="aqe-drp__nav"
            aria-label="Previous month"
            :disabled="!canPrev"
            @click="shiftView(-1)"
          >
            <i class="oxd-icon bi-chevron-left"></i>
          </button>
          <div class="aqe-drp__jump">
            <select
              v-model.number="viewMonth"
              class="aqe-drp__select"
              aria-label="Month"
            >
              <option v-for="(m, i) in monthNames" :key="m" :value="i">
                {{ m }}
              </option>
            </select>
            <select
              v-model.number="viewYear"
              class="aqe-drp__select"
              aria-label="Year"
            >
              <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
            </select>
          </div>
          <button
            type="button"
            class="aqe-drp__nav"
            aria-label="Next month"
            :disabled="!canNext"
            @click="shiftView(1)"
          >
            <i class="oxd-icon bi-chevron-right"></i>
          </button>
        </div>

        <div class="aqe-drp__months">
          <div v-for="m in months" :key="m.key" class="aqe-drp__month">
            <div class="aqe-drp__mtitle" aria-hidden="true">{{ m.title }}</div>
            <table
              class="aqe-drp__grid"
              role="grid"
              :aria-label="m.title"
              @mouseleave="hover = null"
            >
              <thead>
                <tr>
                  <th
                    v-for="d in weekdays"
                    :key="d.short"
                    scope="col"
                    :abbr="d.long"
                  >
                    {{ d.short }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(week, wi) in m.weeks" :key="wi">
                  <td
                    v-for="cell in week"
                    :key="cell.iso"
                    role="gridcell"
                    :aria-selected="
                      cell.inMonth ? String(cell.selected) : undefined
                    "
                  >
                    <button
                      v-if="cell.inMonth"
                      :id="cell.uid"
                      type="button"
                      class="aqe-drp__day"
                      :class="cell.cls"
                      :tabindex="cell.iso === focusIso ? 0 : -1"
                      :disabled="cell.disabled"
                      :aria-label="cell.aria"
                      :title="cell.holiday || undefined"
                      :data-date="cell.iso"
                      @click="pick(cell)"
                      @mouseenter="hover = cell.iso"
                      @focus="focusIso = cell.iso"
                    >
                      {{ cell.day }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="aqe-drp__foot">
          <div class="aqe-drp__legend" aria-hidden="true">
            <span><i class="aqe-drp__dot aqe-drp__dot--hol"></i>Holiday</span>
            <span
              ><i class="aqe-drp__dot aqe-drp__dot--off"></i>Non-working</span
            >
          </div>
          <span class="aqe-drp__summary" aria-live="polite">{{ summary }}</span>
          <button type="button" class="aqe-drp__link" @click="clear">
            Clear
          </button>
          <button
            type="button"
            class="aqe-drp__done"
            :disabled="!from || !to"
            @click="close(true)"
          >
            Done
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import {APIService} from '@ohrm/core/util/services/api.service';

const pad = (n) => String(n).padStart(2, '0');
const iso = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parse = (s) => {
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};
const addDays = (d, n) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const addMonths = (d, n) => {
  const t = new Date(d.getFullYear(), d.getMonth() + n, 1);
  const last = new Date(t.getFullYear(), t.getMonth() + 1, 0).getDate();
  return new Date(t.getFullYear(), t.getMonth(), Math.min(d.getDate(), last));
};
const rand = () => Math.random().toString(36).slice(2, 8);

export default {
  name: 'DateRangePicker',
  props: {
    from: {type: String, default: null},
    to: {type: String, default: null},
    label: {type: String, default: 'Dates'},
    placeholder: {type: String, default: 'Select dates'},
    required: {type: Boolean, default: false},
    error: {type: String, default: null},
    maxSpan: {type: Number, default: 30},
    minDate: {type: String, default: null},
    maxDate: {type: String, default: null},
    disableNonWorking: {type: Boolean, default: true},
    disableHolidays: {type: Boolean, default: true},
  },
  emits: ['update:from', 'update:to', 'change'],

  data() {
    const today = new Date();
    return {
      open: false,
      viewYear: today.getFullYear(),
      viewMonth: today.getMonth(),
      hover: null,
      focusIso: iso(today),
      pendingFrom: null,
      workweek: {},
      holidays: {},
      loadedYears: {},
      uidSeed: rand(),
      triggerId: `drp-${rand()}`,
      errorId: `drp-err-${rand()}`,
      monthNames: Array.from({length: 12}, (_, i) =>
        new Date(2000, i, 1).toLocaleString(undefined, {month: 'long'}),
      ),
      weekdays: Array.from({length: 7}, (_, i) => {
        const d = new Date(2023, 0, 1 + i); // a Sunday
        return {
          short: d.toLocaleString(undefined, {weekday: 'short'}).slice(0, 2),
          long: d.toLocaleString(undefined, {weekday: 'long'}),
        };
      }),
    };
  },

  computed: {
    today() {
      return iso(new Date());
    },
    min() {
      return (
        parse(this.minDate) || new Date(new Date().getFullYear() - 1, 0, 1)
      );
    },
    max() {
      return (
        parse(this.maxDate) || new Date(new Date().getFullYear() + 1, 11, 31)
      );
    },
    years() {
      const out = [];
      for (let y = this.min.getFullYear(); y <= this.max.getFullYear(); y++)
        out.push(y);
      return out;
    },
    anchor() {
      // the start date currently constraining the end date
      return this.pendingFrom || (this.from && !this.to ? this.from : null);
    },
    canPrev() {
      return new Date(this.viewYear, this.viewMonth, 1) > this.min;
    },
    canNext() {
      return new Date(this.viewYear, this.viewMonth + 2, 1) <= this.max;
    },
    months() {
      return [0, 1].map((offset) => {
        const first = new Date(this.viewYear, this.viewMonth + offset, 1);
        return {
          key: `${first.getFullYear()}-${first.getMonth()}`,
          title: first.toLocaleString(undefined, {
            month: 'long',
            year: 'numeric',
          }),
          weeks: this.buildWeeks(first),
        };
      });
    },
    rangeEnd() {
      if (this.anchor && this.hover && this.hover >= this.anchor)
        return this.hover;
      return this.pendingFrom ? null : this.to;
    },
    rangeStart() {
      return this.pendingFrom || this.from;
    },
    workingDays() {
      return this.countWorking(this.from, this.to);
    },
    summary() {
      const s = this.rangeStart;
      const e = this.rangeEnd;
      if (!s) return 'Pick a start date';
      if (!e)
        return `From ${this.pretty(s)} · pick an end date (max ${
          this.maxSpan
        } days)`;
      const n = this.countWorking(s, e);
      return `${n} working ${n === 1 ? 'day' : 'days'}`;
    },
    presets() {
      const t = new Date();
      const next = (dow) => addDays(t, (dow - t.getDay() + 7) % 7 || 7);
      const mon = next(1);
      const mk = (label, a, b) => {
        const ok =
          this.selectable(a) && this.selectable(b) ? [iso(a), iso(b)] : null;
        return {label, range: ok};
      };
      return [
        mk('Tomorrow', addDays(t, 1), addDays(t, 1)),
        mk('Next Monday', mon, mon),
        mk('Next week', mon, addDays(mon, 4)),
        mk('Next 2 weeks', mon, addDays(mon, 11)),
      ];
    },
  },

  watch: {
    open(v) {
      if (v) {
        document.addEventListener('mousedown', this.onOutside, true);
        this.ensureYear(this.viewYear);
        this.ensureYear(this.viewYear + 1);
      } else {
        document.removeEventListener('mousedown', this.onOutside, true);
      }
    },
    viewYear(y) {
      this.ensureYear(y);
      this.ensureYear(y + 1);
    },
  },

  created() {
    this.http = new APIService(window.appGlobal.baseUrl, '');
    this.http
      .request({
        method: 'GET',
        url: '/api/v2/leave/workweek',
        params: {model: 'indexed'},
        validateStatus: (s) => (s >= 200 && s < 300) || s === 403,
      })
      .then(({data}) => {
        if (data?.data) this.workweek = data.data;
      })
      .catch(() => {
        /* no work week: treat every day as working */
      });
  },

  beforeUnmount() {
    document.removeEventListener('mousedown', this.onOutside, true);
  },

  methods: {
    pretty(s) {
      const d = parse(s);
      return d
        ? d.toLocaleDateString(undefined, {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })
        : s;
    },
    ensureYear(y) {
      if (this.loadedYears[y]) return;
      this.loadedYears[y] = true;
      this.http
        .request({
          method: 'GET',
          url: '/api/v2/leave/holidays',
          params: {fromDate: `${y}-01-01`, toDate: `${y}-12-31`},
          validateStatus: (s) => (s >= 200 && s < 300) || s === 403,
        })
        .then(({data}) => {
          if (!Array.isArray(data?.data)) return;
          const next = {...this.holidays};
          data.data.forEach((h) => {
            next[h.date] = {name: h.name, half: h.length !== 0};
          });
          this.holidays = next;
        })
        .catch(() => {
          this.loadedYears[y] = false;
        });
    },
    isNonWorking(d) {
      return this.workweek[d.getDay()] === 8;
    },
    isHalfDay(d) {
      return this.workweek[d.getDay()] === 4;
    },
    selectable(d) {
      if (d < this.min || d > this.max) return false;
      const h = this.holidays[iso(d)];
      if (this.disableHolidays && h && !h.half) return false;
      if (this.disableNonWorking && this.isNonWorking(d)) return false;
      if (this.anchor) {
        const a = parse(this.anchor);
        if (d < a || d > addDays(a, this.maxSpan - 1)) return false;
      }
      return true;
    },
    countWorking(s, e) {
      const a = parse(s);
      const b = parse(e);
      if (!a || !b || b < a) return 0;
      let n = 0;
      for (let d = a; d <= b; d = addDays(d, 1)) {
        const h = this.holidays[iso(d)];
        if (this.isNonWorking(d) || (h && !h.half)) continue;
        n += this.isHalfDay(d) || (h && h.half) ? 0.5 : 1;
      }
      return n;
    },
    buildWeeks(first) {
      const start = addDays(first, -first.getDay());
      const weeks = [];
      const s = this.rangeStart;
      const e = this.rangeEnd;
      for (let w = 0; w < 6; w++) {
        const row = [];
        for (let i = 0; i < 7; i++) {
          const d = addDays(start, w * 7 + i);
          const id = iso(d);
          const inMonth = d.getMonth() === first.getMonth();
          const hol = this.holidays[id];
          const disabled = !this.selectable(d);
          const isStart = id === s;
          const isEnd = !!e && id === e;
          const inRange = !!s && !!e && id > s && id < e;
          row.push({
            iso: id,
            day: d.getDate(),
            inMonth,
            disabled,
            holiday: hol ? hol.name + (hol.half ? ' (half day)' : '') : null,
            selected: isStart || isEnd,
            // generated per render: ids are not a stable hook
            uid: `d${this.uidSeed}${rand().slice(0, 3)}`,
            aria:
              d.toLocaleDateString(undefined, {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              }) +
              (hol ? `, ${hol.name}` : '') +
              (disabled ? ', unavailable' : ''),
            cls: {
              'is-today': id === this.today,
              'is-start': isStart,
              'is-end': isEnd,
              'is-range': inRange,
              'is-holiday': !!hol,
              'is-off': this.isNonWorking(d),
            },
          });
        }
        if (row.some((c) => c.inMonth)) weeks.push(row);
      }
      return weeks;
    },
    toggle() {
      this.open ? this.close() : this.openPicker();
    },
    openPicker() {
      const base = parse(this.from) || new Date();
      this.viewYear = base.getFullYear();
      this.viewMonth = base.getMonth();
      this.focusIso = this.from || this.today;
      this.pendingFrom = null;
      this.open = true;
      this.$nextTick(() => this.focusDay());
    },
    close(focusTrigger = true) {
      this.open = false;
      this.pendingFrom = null;
      this.hover = null;
      if (focusTrigger) this.$nextTick(() => this.$refs.trigger?.focus());
    },
    onOutside(e) {
      if (!this.$refs.root?.contains(e.target)) this.close(false);
    },
    shiftView(n) {
      const d = new Date(this.viewYear, this.viewMonth + n, 1);
      this.viewYear = d.getFullYear();
      this.viewMonth = d.getMonth();
    },
    pick(cell) {
      if (cell.disabled) return;
      if (!this.pendingFrom) {
        this.pendingFrom = cell.iso;
        this.$emit('update:from', cell.iso);
        this.$emit('update:to', null);
        return;
      }
      const from = this.pendingFrom;
      this.pendingFrom = null;
      this.$emit('update:to', cell.iso);
      this.$emit('change', {from, to: cell.iso});
      setTimeout(() => this.close(), 120);
    },
    applyPreset(p) {
      if (!p.range) return;
      this.pendingFrom = null;
      this.$emit('update:from', p.range[0]);
      this.$emit('update:to', p.range[1]);
      this.$emit('change', {from: p.range[0], to: p.range[1]});
      this.close();
    },
    clear() {
      this.pendingFrom = null;
      this.$emit('update:from', null);
      this.$emit('update:to', null);
    },
    focusDay() {
      const el = this.$refs.popup?.querySelector(
        `[data-date="${this.focusIso}"]`,
      );
      if (el && !el.disabled) el.focus();
      else
        this.$refs.popup
          ?.querySelector('.aqe-drp__day:not(:disabled)')
          ?.focus();
    },
    moveFocus(d) {
      if (d < this.min || d > this.max) return;
      this.focusIso = iso(d);
      const first = new Date(this.viewYear, this.viewMonth, 1);
      const last = new Date(this.viewYear, this.viewMonth + 2, 0);
      if (d < first || d > last) {
        this.viewYear = d.getFullYear();
        this.viewMonth = d < first ? d.getMonth() : d.getMonth() - 1;
        if (this.viewMonth < 0) {
          this.viewMonth = 11;
          this.viewYear -= 1;
        }
      }
      this.$nextTick(() => {
        const el = this.$refs.popup?.querySelector(
          `[data-date="${this.focusIso}"]`,
        );
        el?.focus();
      });
    },
    onKeydown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
        return;
      }
      if (e.key === 'Tab') {
        // keep focus inside the dialog
        const f = [
          ...this.$refs.popup.querySelectorAll('button:not(:disabled), select'),
        ].filter((el) => el.tabIndex !== -1);
        const i = f.indexOf(document.activeElement);
        if (e.shiftKey && i === 0) {
          e.preventDefault();
          f[f.length - 1].focus();
        } else if (!e.shiftKey && i === f.length - 1) {
          e.preventDefault();
          f[0].focus();
        }
        return;
      }
      if (!e.target.classList?.contains('aqe-drp__day')) return;
      const cur = parse(this.focusIso) || new Date();
      const map = {
        ArrowLeft: () => addDays(cur, -1),
        ArrowRight: () => addDays(cur, 1),
        ArrowUp: () => addDays(cur, -7),
        ArrowDown: () => addDays(cur, 7),
        Home: () => addDays(cur, -cur.getDay()),
        End: () => addDays(cur, 6 - cur.getDay()),
        PageUp: () => addMonths(cur, e.shiftKey ? -12 : -1),
        PageDown: () => addMonths(cur, e.shiftKey ? 12 : 1),
      };
      if (map[e.key]) {
        e.preventDefault();
        this.moveFocus(map[e.key]());
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.aqe-drp {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-family: Inter, 'Nunito Sans', sans-serif;
}
.aqe-drp__label {
  margin: 0;
}
.aqe-drp__req {
  color: #ef4444;
  margin-left: 2px;
}
.aqe-drp__trigger {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 42px;
  padding: 0 12px;
  border: 1px solid rgba(15, 23, 42, 0.1);
  border-radius: 12px;
  background: #fff;
  color: #0f172a;
  font: 500 14px Inter, sans-serif;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
  &:hover {
    border-color: rgba(15, 23, 42, 0.2);
  }
  &:focus-visible,
  .aqe-drp--open & {
    outline: none;
    border-color: #6366f1;
    box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.14);
  }
  .aqe-drp--invalid & {
    border-color: #ef4444;
  }
}
.aqe-drp__icon {
  color: #6366f1;
  font-size: 15px;
}
.aqe-drp__value {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
}
.aqe-drp__sep {
  color: #94a3b8;
  font-size: 12px;
}
.aqe-drp__muted,
.aqe-drp__placeholder {
  color: #94a3b8;
}
.aqe-drp__placeholder {
  flex: 1;
}
.aqe-drp__badge {
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(5, 150, 105, 0.1);
  color: #047857;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}
.aqe-drp__chev {
  color: #94a3b8;
  font-size: 12px;
  transition: transform 0.2s;
  .aqe-drp--open & {
    transform: rotate(180deg);
  }
}
.aqe-drp__error {
  color: #ef4444;
  font-size: 12px;
}

.aqe-drp__popup {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 1000;
  width: max-content;
  max-width: calc(100vw - 32px);
  padding: 14px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.06),
    0 32px 64px -24px rgba(15, 23, 42, 0.35);
}
.aqe-drp-pop-enter-active,
.aqe-drp-pop-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.aqe-drp-pop-enter-from,
.aqe-drp-pop-leave-to {
  opacity: 0;
  transform: translateY(-4px) scale(0.99);
}

.aqe-drp__presets {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding-bottom: 12px;
  margin-bottom: 10px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.07);
}
.aqe-drp__preset {
  height: 30px;
  padding: 0 12px;
  border: 0;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.05);
  color: #334155;
  font: 600 12.5px Inter, sans-serif;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: #0f172a;
    color: #fff;
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.aqe-drp__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}
.aqe-drp__nav {
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: #334155;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: rgba(15, 23, 42, 0.06);
  }
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
}
.aqe-drp__jump {
  display: flex;
  gap: 6px;
}
.aqe-drp__select {
  height: 32px;
  padding: 0 8px;
  border: 1px solid rgba(15, 23, 42, 0.1);
  border-radius: 9px;
  background: #fff;
  color: #0f172a;
  font: 600 13px Inter, sans-serif;
  cursor: pointer;
}

.aqe-drp__months {
  display: flex;
  gap: 22px;
}
.aqe-drp__mtitle {
  margin: 4px 0 6px;
  text-align: center;
  font: 700 13px Inter, sans-serif;
  color: #0f172a;
}
.aqe-drp__grid {
  border-collapse: separate;
  border-spacing: 0 2px;
  th {
    width: 38px;
    padding: 4px 0;
    font: 600 11px Inter, sans-serif;
    color: #94a3b8;
    text-transform: uppercase;
  }
  td {
    padding: 0;
    text-align: center;
  }
}
.aqe-drp__day {
  position: relative;
  width: 38px;
  height: 36px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: #0f172a;
  font: 500 13px Inter, sans-serif;
  cursor: pointer;
  transition: background-color 0.12s, color 0.12s;
  &:hover:not(:disabled) {
    background: rgba(99, 102, 241, 0.1);
  }
  &:focus-visible {
    outline: 2px solid #6366f1;
    outline-offset: -2px;
  }
  &.is-today::after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: 5px;
    width: 4px;
    height: 4px;
    margin-left: -2px;
    border-radius: 50%;
    background: #6366f1;
  }
  &.is-off {
    color: #cbd5e1;
  }
  &.is-holiday {
    color: #b45309;
    background: rgba(245, 158, 11, 0.1);
  }
  &:disabled {
    cursor: not-allowed;
    text-decoration: line-through;
    text-decoration-color: rgba(148, 163, 184, 0.6);
    opacity: 0.55;
  }
  &.is-range {
    border-radius: 0;
    background: rgba(99, 102, 241, 0.1);
    color: #3730a3;
  }
  &.is-start,
  &.is-end {
    background: #0f172a;
    color: #fff;
    &::after {
      background: #a7f3d0;
    }
  }
}
.aqe-drp__foot {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
  padding-top: 12px;
  border-top: 1px solid rgba(15, 23, 42, 0.07);
}
.aqe-drp__legend {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #64748b;
  span {
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }
}
.aqe-drp__dot {
  width: 8px;
  height: 8px;
  border-radius: 3px;
  &--hol {
    background: rgba(245, 158, 11, 0.45);
  }
  &--off {
    background: #e2e8f0;
  }
}
.aqe-drp__summary {
  flex: 1;
  text-align: right;
  font-size: 12.5px;
  font-weight: 600;
  color: #334155;
}
.aqe-drp__link {
  border: 0;
  background: none;
  color: #64748b;
  font: 600 13px Inter, sans-serif;
  cursor: pointer;
  &:hover {
    color: #0f172a;
  }
}
.aqe-drp__done {
  height: 34px;
  padding: 0 16px;
  border: 0;
  border-radius: 10px;
  background: #0f172a;
  color: #fff;
  font: 600 13px Inter, sans-serif;
  cursor: pointer;
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

@media (max-width: 720px) {
  .aqe-drp__months {
    flex-direction: column;
  }
  .aqe-drp__month + .aqe-drp__month {
    display: none;
  }
  .aqe-drp__legend {
    display: none;
  }
}
</style>
